import { FC } from "react";
import { Form } from "react-bootstrap";
import {
  addSeperator,
  removeNonNumeric,
} from "../../../utils/currencyValidator";

export enum RateType {
  USER = "user",
  AGENT = "agent",
}

export enum RateCategory {
  INFANT = "infant",
  CHILD = "child",
  ADULT = "adult",
}

export type Rate = {
  category: RateCategory; // <2, <11, >11
  basePrice: number | string | undefined;
  margin: number | string | undefined;
  twoWay: boolean;
  isForeigner: boolean;
  type: RateType;
  isRestricted?: boolean;
};

export interface RatesProps {
  sources: {
    rate: Rate;
    error: {
      basePrice: string;
    };
  }[];
  onChange: (data: Rate, name: "basePrice" | "margin", value: string) => void;
  outboundTax?: number | null;
  outboundSurcharge?: number | null;
  returnTax?: number | null;
  returnSurcharge?: number | null;
  hasRestrictedReturn?: boolean;
  restrictedReturnTax?: number | null;
  restrictedReturnSurcharge?: number | null;
}

const CATEGORY_LABELS: Record<RateCategory, string> = {
  [RateCategory.INFANT]: "Infant",
  [RateCategory.CHILD]: "Child",
  [RateCategory.ADULT]: "Adult",
};

const Rates: FC<RatesProps> = (props) => {
  const categories = [RateCategory.INFANT, RateCategory.CHILD, RateCategory.ADULT];

  const hasForeigner = props.sources.some((s) => s.rate.isForeigner);
  const hasTwoWay = props.sources.some((s) => s.rate.twoWay && !(s.rate.isRestricted ?? false));
  const hasAgentPrice = props.sources.some((s) => s.rate.type === RateType.AGENT);

  const findSource = (
    isForeigner: boolean,
    twoWay: boolean,
    category: RateCategory,
    type: RateType,
    isRestricted: boolean = false
  ) => {
    return props.sources.find(
      (s) =>
        s.rate.isForeigner === isForeigner &&
        s.rate.twoWay === twoWay &&
        s.rate.category === category &&
        s.rate.type === type &&
        (s.rate.isRestricted ?? false) === isRestricted
    );
  };

  const formatPrice = (basePrice: number | string | undefined): string => {
    if (basePrice === undefined || basePrice === null) return "";
    const num = parseInt(basePrice.toString());
    if (Number.isNaN(num)) return "";
    return addSeperator(num.toString());
  };

  const feeValue = (v: number | null | undefined): number => (v == null ? 0 : (v ?? 0));

  const getTotalLabel = (
    basePrice: number | string | undefined,
    twoWay: boolean,
    isRestricted: boolean = false
  ): string => {
    if (basePrice === undefined || basePrice === null) return "";
    const num = parseInt(basePrice.toString());
    if (Number.isNaN(num)) return "";
    const outbound = feeValue(props.outboundTax) + feeValue(props.outboundSurcharge);
    let returnFees: number;
    if (isRestricted) {
      returnFees = feeValue(props.restrictedReturnTax) + feeValue(props.restrictedReturnSurcharge);
    } else if (twoWay) {
      returnFees = feeValue(props.returnTax) + feeValue(props.returnSurcharge);
    } else {
      returnFees = 0;
    }
    return `Rp ${addSeperator((num + outbound + returnFees).toString())}`;
  };

  const fmtFee = (v: number | null | undefined, label: string): string =>
    v === null ? `${label}: —` : `${label}: Rp ${addSeperator(feeValue(v).toString())}`;

  const getSummaryText = (twoWay: boolean, isRestricted: boolean): string => {
    const parts = [
      fmtFee(props.outboundTax, "Tax"),
      fmtFee(props.outboundSurcharge, "Surcharge"),
    ];
    if (twoWay) {
      if (isRestricted) {
        parts.push(fmtFee(props.restrictedReturnTax, "Return tax"));
        parts.push(fmtFee(props.restrictedReturnSurcharge, "Return surcharge"));
      } else {
        parts.push(fmtFee(props.returnTax, "Return tax"));
        parts.push(fmtFee(props.returnSurcharge, "Return surcharge"));
      }
    }
    return parts.join(" · ");
  };

  const onInputChange = (
    rate: Rate,
    name: "basePrice" | "margin",
    value: string
  ) => {
    props.onChange(rate, name, removeNonNumeric(value));
  };

  const renderCard = (
    isForeigner: boolean,
    twoWay: boolean,
    category: RateCategory,
    isRestricted: boolean = false
  ) => {
    const userSource = findSource(isForeigner, twoWay, category, RateType.USER, isRestricted);
    const agentSource = hasAgentPrice
      ? findSource(isForeigner, twoWay, category, RateType.AGENT, isRestricted)
      : undefined;

    if (!userSource) return null;

    const totalLabel = getTotalLabel(userSource.rate.basePrice, twoWay, isRestricted);
    const agentTotalLabel = agentSource
      ? getTotalLabel(agentSource.rate.basePrice, twoWay, isRestricted)
      : "";
    const summaryText = getSummaryText(twoWay, isRestricted);

    return (
      <div
        key={`card-${isForeigner}-${twoWay}-${isRestricted}-${category}`}
        className="border rounded p-3 rate-container mb-3"
      >
        <div className="fw-600 mb-2">{CATEGORY_LABELS[category]}</div>
        {totalLabel && (
          <div className="text-success mb-2" style={{ fontSize: ".75rem" }}>
            {totalLabel}
          </div>
        )}
        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: ".75rem" }} className="mb-1">
            Base price
          </Form.Label>
          <Form.Control
            className={userSource.error?.basePrice ? "border-error-2" : ""}
            name="basePrice"
            type="text"
            placeholder="Required"
            value={formatPrice(userSource.rate.basePrice)}
            onChange={(e) =>
              onInputChange(userSource.rate, "basePrice", e.currentTarget.value)
            }
          />
          {userSource.error?.basePrice && (
            <Form.Text className="text-error-2">
              {userSource.error.basePrice}
            </Form.Text>
          )}
          <div className="text-muted" style={{ fontSize: ".7rem", marginTop: 2 }}>
            {summaryText}
          </div>
        </Form.Group>
        {hasAgentPrice && agentSource && (
          <>
            {agentTotalLabel && (
              <div className="text-success mb-2" style={{ fontSize: ".75rem" }}>
                {agentTotalLabel}
              </div>
            )}
            <Form.Group>
              <Form.Label style={{ fontSize: ".75rem" }} className="mb-1">
                Agent price
              </Form.Label>
              <Form.Control
                className={agentSource.error?.basePrice ? "border-error-2" : ""}
                name="basePrice"
                type="text"
                placeholder="Required"
                value={formatPrice(agentSource.rate.basePrice)}
                onChange={(e) =>
                  onInputChange(agentSource.rate, "basePrice", e.currentTarget.value)
                }
              />
              {agentSource.error?.basePrice && (
                <Form.Text className="text-error-2">
                  {agentSource.error.basePrice}
                </Form.Text>
              )}
              <div className="text-muted" style={{ fontSize: ".7rem", marginTop: 2 }}>
                {summaryText}
              </div>
            </Form.Group>
          </>
        )}
      </div>
    );
  };

  const renderSection = (isForeigner: boolean) => {
    const roundTripLabel = props.hasRestrictedReturn ? "Round-Trip" : "Two Way";
    return (
      <div key={`section-${isForeigner}`} className="mb-4">
        {hasForeigner && (
          <h6 className="text-decoration-underline mb-3">
            {isForeigner ? "Foreigner" : "Local"}
          </h6>
        )}
        <div className="mb-3">
          {(hasTwoWay || props.hasRestrictedReturn) && (
            <div className="fw-500 mb-2" style={{ fontSize: ".875rem" }}>
              One Way
            </div>
          )}
          <div className="rates-input-container" style={{ gap: "15px" }}>
            {categories.map((cat) => renderCard(isForeigner, false, cat))}
          </div>
        </div>
        {hasTwoWay && (
          <div className="mb-3">
            <div className="fw-500 mb-2" style={{ fontSize: ".875rem" }}>
              {roundTripLabel}
            </div>
            <div className="rates-input-container" style={{ gap: "15px" }}>
              {categories.map((cat) => renderCard(isForeigner, true, cat, false))}
            </div>
          </div>
        )}
        {props.hasRestrictedReturn && (
          <div>
            <div className="fw-500 mb-2" style={{ fontSize: ".875rem" }}>
              Restricted Return
            </div>
            <div className="rates-input-container" style={{ gap: "15px" }}>
              {categories.map((cat) => renderCard(isForeigner, true, cat, true))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {renderSection(false)}
      {hasForeigner && renderSection(true)}
    </div>
  );
};

export default Rates;
